import React, { useEffect, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = { title: "", slug: "", excerpt: "", content: "", category: "AI Tools", tags: "", author: "BINAREX Team", featured_image: "", meta_title: "", meta_description: "", published: true };
  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing, tags: typeof editing.tags === "string" ? editing.tags.split(",").map((s) => s.trim()).filter(Boolean) : editing.tags };
      if (editing.isNew) {
        delete payload.isNew; delete payload.slug;
        if (editing.slug) payload.slug = editing.slug;
        await api.post("/admin/blog", payload);
      } else {
        await api.patch(`/admin/blog/${editing.slug}`, payload);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${slug}`); toast.success("Deleted"); load();
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="bx-heading text-3xl font-extrabold">Blog</h1>
        <button data-testid="admin-blog-new" onClick={() => setEditing({ ...blank, isNew: true })} className="bx-btn-primary"><Lucide.Plus size={16} /> New Post</button>
      </div>
      <div className="bx-card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="text-left p-3">Title</th><th className="text-left p-3">Category</th><th className="text-left p-3">Status</th><th className="text-left p-3">Date</th><th className="text-right p-3">Actions</th></tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-white/5">
                <td className="p-3">{p.title}</td>
                <td className="p-3 text-slate-400">{p.category}</td>
                <td className="p-3"><span className="bx-chip" style={{ fontSize: 10 }}>{p.published ? "published" : "draft"}</span></td>
                <td className="p-3 text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="p-3 text-right space-x-2">
                  <button onClick={() => setEditing({ ...p, tags: (p.tags || []).join(", ") })} className="bx-btn-ghost text-xs">Edit</button>
                  <button onClick={() => del(p.slug)} className="bx-btn-ghost text-xs text-rose-300">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5 overflow-auto">
          <div className="bx-card p-6 max-w-2xl w-full my-10">
            <div className="bx-heading text-lg font-bold">{editing.isNew ? "New Post" : `Edit: ${editing.title}`}</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title" className="bx-input col-span-2" />
              <input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="Slug (optional)" className="bx-input" />
              <input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="Category" className="bx-input" />
              <input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} placeholder="Author" className="bx-input" />
              <input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="Tags (comma separated)" className="bx-input" />
              <input value={editing.featured_image || ""} onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} placeholder="Featured image URL" className="bx-input col-span-2" />
              <input value={editing.meta_title || ""} onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} placeholder="Meta title (SEO)" className="bx-input col-span-2" />
              <input value={editing.meta_description || ""} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} placeholder="Meta description (SEO)" className="bx-input col-span-2" />
              <textarea rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} placeholder="Excerpt" className="bx-input col-span-2 resize-y" />
              <textarea rows={10} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} placeholder="Content (Markdown supported)" className="bx-input col-span-2 resize-y font-mono text-xs" />
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published</label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="bx-btn-ghost">Cancel</button>
              <button data-testid="admin-blog-save" onClick={save} className="bx-btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export function AdminPages() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/pages").then((r) => setPages(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.patch(`/admin/pages/${editing.slug}`, { title: editing.title, content: editing.content }); toast.success("Saved"); setEditing(null); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Pages</h1>
      <p className="text-slate-400 text-sm mt-1">Edit Privacy, Terms, Refund, and About content.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {pages.map((p) => (
          <button key={p.slug} onClick={() => setEditing({ ...p })} className="bx-card p-5 text-left">
            <div className="text-xs text-purple-300 uppercase tracking-wider">{p.slug}</div>
            <div className="bx-heading text-lg font-bold mt-1">{p.title}</div>
            <div className="text-xs text-slate-500 mt-2">Updated: {new Date(p.updated_at).toLocaleDateString()}</div>
          </button>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5 overflow-auto">
          <div className="bx-card p-6 max-w-3xl w-full my-10">
            <div className="bx-heading text-lg font-bold">Edit: {editing.slug}</div>
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bx-input mt-3" />
            <textarea rows={20} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="bx-input mt-3 resize-y font-mono text-xs" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="bx-btn-ghost">Cancel</button>
              <button onClick={save} className="bx-btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export function AdminFAQ() {
  const [faqs, setFaqs] = useState([]);
  const [newF, setNewF] = useState({ question: "", answer: "", category: "General" });
  const load = () => api.get("/admin/faq").then((r) => setFaqs(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!newF.question.trim()) return;
    try { await api.post("/admin/faq", newF); toast.success("Added"); setNewF({ question: "", answer: "", category: "General" }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/faq/${id}`); load(); };
  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">FAQ</h1>
      <div className="bx-card p-5 mt-6 space-y-3">
        <input value={newF.question} onChange={(e) => setNewF({ ...newF, question: e.target.value })} placeholder="Question" className="bx-input" />
        <textarea rows={3} value={newF.answer} onChange={(e) => setNewF({ ...newF, answer: e.target.value })} placeholder="Answer" className="bx-input resize-y" />
        <input value={newF.category} onChange={(e) => setNewF({ ...newF, category: e.target.value })} placeholder="Category" className="bx-input" />
        <button onClick={add} className="bx-btn-primary">Add FAQ</button>
      </div>
      <div className="mt-6 space-y-3">
        {faqs.map((f) => (
          <div key={f.id} className="bx-card p-4 flex items-start justify-between gap-3">
            <div>
              <div className="text-xs text-purple-300">{f.category}</div>
              <div className="font-semibold mt-1">{f.question}</div>
              <p className="text-sm text-slate-400 mt-1">{f.answer}</p>
            </div>
            <button onClick={() => del(f.id)} className="bx-btn-ghost text-xs text-rose-300">Delete</button>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

export function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [reply, setReply] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTplPicker, setShowTplPicker] = useState(false);

  const load = async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (q) params.q = q;
    const [a, b] = await Promise.all([
      api.get("/admin/tickets", { params }),
      api.get("/admin/tickets-stats"),
    ]);
    setTickets(a.data);
    setStats(b.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { api.get("/admin/ticket-templates").then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    api.get(`/admin/tickets/${selectedId}`).then((r) => setSelected(r.data));
  }, [selectedId]);

  const sendReply = async () => {
    if (!reply.trim()) return toast.error("Reply is empty");
    setBusy(true);
    try {
      await api.post(`/admin/tickets/${selectedId}/reply`, { body: reply.trim(), send_email: sendEmail });
      setReply("");
      toast.success(sendEmail ? "Reply sent" : "Reply saved");
      const r = await api.get(`/admin/tickets/${selectedId}`);
      setSelected(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/tickets/${selectedId}/status`, { status });
      toast.success(`Marked ${status}`);
      const r = await api.get(`/admin/tickets/${selectedId}`);
      setSelected(r.data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteTicket = async () => {
    if (!window.confirm("Delete this ticket permanently?")) return;
    try {
      await api.delete(`/admin/tickets/${selectedId}`);
      setSelectedId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const fetchNow = async () => {
    try {
      await api.post("/admin/imap/fetch-now");
      toast.success("IMAP fetch triggered");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const STATUS_COLOR = {
    open: "text-emerald-300",
    pending: "text-amber-300",
    resolved: "text-slate-400",
    closed: "text-slate-500",
  };

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="bx-heading text-3xl font-extrabold">Support Tickets</h1>
          <p className="text-slate-400 text-sm mt-1">Inbox for customer support. Replies are sent via SMTP and tracked here.</p>
        </div>
        <button data-testid="admin-tickets-fetch-now" onClick={fetchNow} className="bx-btn-ghost text-xs"><Lucide.Inbox size={14} /> Fetch new emails</button>
      </div>

      {stats && (
        <div className="flex flex-wrap gap-2 mt-4 text-xs">
          <button onClick={() => setStatusFilter("")} className={`bx-chip ${statusFilter === "" ? "ring-1 ring-purple-400" : ""}`}>All · {stats.total}</button>
          {["open", "pending", "resolved", "closed"].map((s) => (
            <button key={s} data-testid={`tickets-filter-${s}`} onClick={() => setStatusFilter(s)} className={`bx-chip capitalize ${statusFilter === s ? "ring-1 ring-purple-400" : ""}`}>{s} · {stats.by_status?.[s] || 0}</button>
          ))}
          <div className="relative ml-auto">
            <Lucide.Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search subject, email, ticket #…" className="bx-input pl-7 text-xs py-1.5 w-64" />
          </div>
        </div>
      )}

      <div className="mt-5 grid lg:grid-cols-[380px_1fr] gap-4">
        {/* List pane */}
        <div className="bx-card p-2 max-h-[78vh] overflow-y-auto bx-scrollbar" data-testid="admin-tickets-list">
          {tickets.map((t) => (
            <button
              key={t.id}
              data-testid={`ticket-row-${t.ticket_no || t.id}`}
              onClick={() => setSelectedId(t.id)}
              className={`w-full text-left p-3 rounded-lg ${selectedId === t.id ? "bg-purple-500/10 border border-purple-400/30" : "hover:bg-white/5 border border-transparent"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono text-purple-300">{t.ticket_no || t.id.slice(0, 6)}</span>
                <span className={`text-[10px] uppercase tracking-wider ${STATUS_COLOR[t.status] || "text-slate-400"}`}>{t.status}</span>
              </div>
              <div className="text-sm font-semibold mt-0.5 line-clamp-1">{t.subject || "(no subject)"}</div>
              <div className="text-[11px] text-slate-400 line-clamp-1">{t.user_email}</div>
              <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{t.preview}</div>
              <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                {t.source === "email" && <Lucide.Mail size={10} />}
                {t.source === "web" && <Lucide.Globe size={10} />}
                {(t.message_count || 1)} msg · {new Date(t.last_message_at || t.created_at).toLocaleString()}
              </div>
            </button>
          ))}
          {!tickets.length && <div className="text-slate-500 text-sm p-6 text-center">No tickets.</div>}
        </div>

        {/* Detail pane */}
        <div className="bx-card p-5 max-h-[78vh] overflow-y-auto bx-scrollbar" data-testid="admin-ticket-detail">
          {!selected && <div className="text-slate-500 text-sm py-20 text-center">Select a ticket to view the conversation.</div>}
          {selected && (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-mono text-purple-300">{selected.ticket_no || selected.id}</div>
                  <h2 className="bx-heading text-xl font-bold mt-0.5">{selected.subject}</h2>
                  <div className="text-xs text-slate-400 mt-1">
                    {selected.customer_name || selected.user_email} · <span className="font-mono text-slate-300">{selected.user_email}</span>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <select data-testid="ticket-status-select" value={selected.status} onChange={(e) => setStatus(e.target.value)} className="bx-input text-xs py-1.5">
                    <option value="open">open</option>
                    <option value="pending">pending</option>
                    <option value="resolved">resolved</option>
                    <option value="closed">closed</option>
                  </select>
                  <button data-testid="ticket-delete" onClick={deleteTicket} className="bx-btn-ghost text-xs text-rose-300"><Lucide.Trash2 size={14} /></button>
                </div>
              </div>

              {selected.pending_password_set && (
                <div className="mt-3 text-[11px] p-2 rounded bg-amber-500/10 border border-amber-500/20 text-amber-200">
                  <Lucide.AlertCircle size={11} className="inline mr-1" /> Customer hasn't set a password yet — they were emailed a setup link.
                </div>
              )}

              <div className="mt-5 space-y-3">
                {(selected.messages || []).map((m) => (
                  <div key={m.id} data-testid={`ticket-msg-${m.id}`} className={`p-3 rounded-lg ${m.from === "admin" ? "bg-purple-500/10 border border-purple-400/20" : "bg-white/5 border border-white/10"}`}>
                    <div className="flex items-center justify-between text-[11px] text-slate-400">
                      <span className={m.from === "admin" ? "text-purple-300 font-semibold" : "text-emerald-300 font-semibold"}>
                        {m.from === "admin" ? "Support" : "Customer"} · {m.author_name}
                      </span>
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-slate-100 whitespace-pre-wrap mt-1.5">{m.body}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-slate-400">Reply</label>
                  <div className="relative">
                    <button data-testid="ticket-templates-toggle" type="button" onClick={() => setShowTplPicker(!showTplPicker)} className="text-[11px] text-purple-300 hover:text-purple-200 flex items-center gap-1">
                      <Lucide.MessageSquareQuote size={11} /> Quick replies
                    </button>
                    {showTplPicker && (
                      <div data-testid="ticket-templates-picker" className="absolute right-0 top-full mt-1 w-80 bx-card p-2 z-20 max-h-64 overflow-y-auto bx-scrollbar border border-purple-500/30 shadow-2xl">
                        {templates.map((t) => (
                          <button
                            key={t.id}
                            data-testid={`ticket-template-${t.id.slice(0, 8)}`}
                            type="button"
                            onClick={() => {
                              const name = (selected.customer_name || selected.user_email.split("@")[0]).split(" ")[0];
                              const body = (t.body || "").replaceAll("{{name}}", name);
                              setReply(reply ? `${reply}\n\n${body}` : body);
                              setShowTplPicker(false);
                            }}
                            className="w-full text-left p-2 rounded hover:bg-purple-500/10 border-b border-white/5 last:border-b-0"
                          >
                            <div className="text-xs font-semibold text-purple-300 flex items-center gap-1">
                              <Lucide.MessageSquareReply size={10} /> {t.title}
                            </div>
                            <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{t.body}</div>
                          </button>
                        ))}
                        {!templates.length && <div className="text-slate-500 text-xs p-3 text-center">No templates. Add some at <code className="text-purple-300">/admin/ticket-templates</code></div>}
                      </div>
                    )}
                  </div>
                </div>
                <textarea data-testid="ticket-reply-input" rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply, or click 'Quick replies' to insert a template…" className="bx-input resize-y w-full" />
                <div className="mt-2 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-slate-400">
                    <input data-testid="ticket-reply-send-email" type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                    Send via email (recommended)
                  </label>
                  <button data-testid="ticket-reply-send" onClick={sendReply} disabled={busy || !reply.trim()} className="bx-btn-primary text-sm disabled:opacity-50">
                    {busy ? <><Lucide.Loader2 className="animate-spin" size={14} /> Sending…</> : <><Lucide.Send size={14} /> Send reply</>}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export function AdminMessages() {
  const [m, setM] = useState([]);
  useEffect(() => { api.get("/admin/contact-messages").then((r) => setM(r.data)); }, []);
  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Contact Messages</h1>
      <div className="mt-6 space-y-3">
        {m.map((x) => (
          <div key={x.id} className="bx-card p-4">
            <div className="text-xs text-slate-400">{x.email} · {x.name}</div>
            <div className="font-semibold mt-0.5">{x.subject}</div>
            <p className="text-sm text-slate-300 mt-2">{x.message}</p>
            <div className="text-xs text-slate-500 mt-2">{new Date(x.created_at).toLocaleString()}</div>
          </div>
        ))}
        {!m.length && <div className="text-slate-500 text-sm">No messages.</div>}
      </div>
    </AdminLayout>
  );
}
